import { isPast, isEqual } from "date-fns";
import React, { useState, useMemo } from "react";

import { CountdownTimer as CountdownTimerUI } from "@bared/ui";

import { useRichText } from "~/hooks/useRichText";
import { useRouteLoaderData } from "~/hooks/useRouteLoaderData";

export function CountdownTimer({
  collection,
  enableCloseButton = true,
}: {
  collection?: string;
  enableCloseButton?: boolean;
}) {
  const { countdownTimers } = useRouteLoaderData("routes/$site");
  const { parseContent } = useRichText();

  const [isClosed, setIsClosed] = useState(
    enableCloseButton && "undefined" !== typeof window
      ? window.sessionStorage.getItem("hide-countdown-timer") === "true"
      : false,
  );

  function onClose() {
    setIsClosed(true);
    sessionStorage.setItem("hide-countdown-timer", "true");
  }

  // Force component to re-render when timer expires.
  const [renderCount, setRenderCount] = useState(0);

  const activeTimer = useMemo(() => {
    if (!countdownTimers) return null;
    return countdownTimers
      .map((timer: any) => {
        const timerEnds = new Date(timer.timer);

        return {
          ...timer,
          timerEnds,
          isPast: isPast(timer.timer) || isEqual(timer.timer, Date.now()),
        };
      })
      .filter((timer: any) => !timer.isPast) // Ignore timers already ended.
      .sort((a: any, b: any) => a.timerEnds - b.timerEnds)[0]; // Get next upcoming timer.
  }, [countdownTimers, renderCount]);

  // At least one collection has been set on the timer, and this collection is not
  // in it, thus this component should be hidden.
  const collectionDisabled =
    !!collection && // Collection provided to this component.
    !!activeTimer?.collections && // There is an active timer with collections.
    0 < activeTimer.collections.length && // At least one collection has been set.
    // Given collection is not in the array.
    !activeTimer.collections.find(
      (c: any) => c.shopify?.shopifyHandle === collection,
    );

  if (isClosed || !activeTimer || collectionDisabled) return null;

  return (
    <CountdownTimerUI
      endDate={activeTimer.timer}
      title={parseContent(activeTimer.heading)}
      mobileTitle={parseContent(activeTimer.pdpHeading)}
      textColor={activeTimer.textColor?.hex}
      backgroundColor={activeTimer.backgroundColour?.hex}
      link={
        activeTimer.link?.customContentLink?.[0]?.link
          ? {
              label: activeTimer.link.title,
              href: activeTimer.link.customContentLink[0].link,
            }
          : undefined
      }
      onClose={enableCloseButton ? onClose : undefined}
      onExpire={() => setRenderCount((count) => count + 1)}
    />
  );
}
